import dayjs from "dayjs";
import {
  CameraAndAnalysisConfigDiff,
  CameraMemo,
  CameraPosition,
  CameraWithAnalysisConfig,
  CountingLine,
  FloorPlanDiff,
  FloorPlanMemoDiagram,
  FloorPlanMemoText,
  IntervalZone,
} from "~/_legacy/types/analysis";
import { DateRange, User } from "~/_legacy/types/common";
import { Coordinate } from "~/_legacy/types/shape";

const areCameraMemosEqual = (memoA: CameraMemo, memoB: CameraMemo) => {
  if (memoA.cameraId !== memoB.cameraId) {
    return false;
  }
  if (memoA.type !== memoB.type) {
    return false;
  }
  if (memoA.content !== memoB.content) {
    return false;
  }
  if (!areUsersEqual(memoA.author, memoB.author)) {
    return false;
  }
  if (memoA.updatedAt !== memoB.updatedAt) {
    return false;
  }
  if (memoA.floorPlanId !== memoB.floorPlanId) {
    return false;
  }
  if (memoA.positionForAnalysisSnapshot !== memoB.positionForAnalysisSnapshot) {
    return false;
  } else if (
    memoA.positionForAnalysisSnapshot &&
    memoB.positionForAnalysisSnapshot &&
    (!areFloatsEqual(memoA.positionForAnalysisSnapshot.x, memoB.positionForAnalysisSnapshot.x) ||
      !areFloatsEqual(memoA.positionForAnalysisSnapshot.y, memoB.positionForAnalysisSnapshot.y))
  ) {
    return false;
  }
  if (memoA.positionForFloorPlan !== memoB.positionForFloorPlan) {
    return false;
  } else if (
    memoA.positionForFloorPlan &&
    memoB.positionForFloorPlan &&
    (!areFloatsEqual(memoA.positionForFloorPlan.x, memoB.positionForFloorPlan.x) ||
      !areFloatsEqual(memoA.positionForFloorPlan.y, memoB.positionForFloorPlan.y))
  ) {
    return false;
  }
  return true;
};

const areCountingLinesEqual = (countingLineA: CountingLine, countingLineB: CountingLine) => {
  if (countingLineA.key !== countingLineB.key) {
    return false;
  }
  if (countingLineA.cameraId !== countingLineB.cameraId) {
    return false;
  }
  if (countingLineA.analysisConfig && countingLineB.analysisConfig) {
    if (countingLineA.analysisConfig.name !== countingLineB.analysisConfig.name) {
      return false;
    }
    if (
      !areCoordinatesEqual(
        countingLineA.analysisConfig.coordinates,
        countingLineB.analysisConfig.coordinates
      )
    ) {
      return false;
    }
    if (countingLineA.analysisConfig.eventType !== countingLineB.analysisConfig.eventType) {
      return false;
    }
  } else if (countingLineA.analysisConfig !== countingLineB.analysisConfig) {
    return false;
  }
  if (countingLineA.floorPlanConfig && countingLineB.floorPlanConfig) {
    if (
      !areCoordinatesEqual(
        countingLineA.floorPlanConfig.coordinates,
        countingLineB.floorPlanConfig.coordinates
      )
    ) {
      return false;
    }
  } else if (countingLineA.floorPlanConfig !== countingLineB.floorPlanConfig) {
    return false;
  }
  return true;
};

const areFloorPlanMemoDiagramsEqual = (
  origin: FloorPlanMemoDiagram,
  local: FloorPlanMemoDiagram
) => {
  if (origin.style.fill !== local.style.fill) {
    return false;
  }
  if (origin.style.borderColor !== local.style.borderColor) {
    return false;
  }
  if (!areFloatsEqual(origin.style.opacity, local.style.opacity)) {
    return false;
  }
  if (!areCoordinatesEqual(origin.coordinates, local.coordinates)) {
    return false;
  }

  return true;
};

const areFloorPlanMemoTextsEqual = (origin: FloorPlanMemoText, local: FloorPlanMemoText) => {
  if (origin.content !== local.content) {
    return false;
  }
  if (origin.style.color !== local.style.color) {
    return false;
  }
  if (!areFloatsEqual(origin.style.width, local.style.width)) {
    return false;
  }
  if (!areFloatsEqual(origin.style.opacity, local.style.opacity)) {
    return false;
  }
  if (origin.style.fontSize !== local.style.fontSize) {
    return false;
  }
  if (origin.style.hasStroke !== local.style.hasStroke) {
    return false;
  }
  if (
    !areFloatsEqual(origin.position.x, local.position.x) ||
    !areFloatsEqual(origin.position.y, local.position.y)
  ) {
    return false;
  }

  return true;
};

const areIntervalZonesEqual = (intervalZoneA: IntervalZone, intervalZoneB: IntervalZone) => {
  if (intervalZoneA.key !== intervalZoneB.key) {
    return false;
  }
  if (intervalZoneA.cameraId !== intervalZoneB.cameraId) {
    return false;
  }
  if (intervalZoneA.analysisConfig && intervalZoneB.analysisConfig) {
    if (intervalZoneA.analysisConfig.name !== intervalZoneB.analysisConfig.name) {
      return false;
    }
    if (intervalZoneA.analysisConfig.eventType !== intervalZoneB.analysisConfig.eventType) {
      return false;
    }
    if (
      !areCoordinatesEqual(
        intervalZoneA.analysisConfig.coordinates,
        intervalZoneB.analysisConfig.coordinates
      )
    ) {
      return false;
    }
  } else if (intervalZoneA.analysisConfig !== intervalZoneB.analysisConfig) {
    return false;
  }
  if (intervalZoneA.floorPlanConfig && intervalZoneB.floorPlanConfig) {
    if (
      !areCoordinatesEqual(
        intervalZoneA.floorPlanConfig.coordinates,
        intervalZoneB.floorPlanConfig.coordinates
      )
    ) {
      return false;
    }
  } else if (intervalZoneA.floorPlanConfig !== intervalZoneB.floorPlanConfig) {
    return false;
  }
  return true;
};

const arePositionsEqual = (positionA: CameraPosition, positionB: CameraPosition) => {
  return (
    areFloatsEqual(positionA.x, positionB.x) &&
    areFloatsEqual(positionA.y, positionB.y) &&
    areFloatsEqual(positionA.rotation, positionB.rotation)
  );
};

const areUsersEqual = (userA: User | null, userB: User | null) => {
  if (userA === null || userB === null) {
    return userA === userB;
  }
  return (
    userA.name === userB.name &&
    userA.email === userB.email &&
    userA.isStaff === userB.isStaff &&
    userA.client.uid === userB.client.uid
  );
};

export const areCoordinatesEqual = (coordinatesA: Coordinate[], coordinatesB: Coordinate[]) => {
  if (coordinatesA.length !== coordinatesB.length) {
    return false;
  }
  for (let i = 0; i < coordinatesA.length; i += 1) {
    if (
      !areFloatsEqual(coordinatesA[i].x, coordinatesB[i].x) ||
      !areFloatsEqual(coordinatesA[i].y, coordinatesB[i].y)
    ) {
      return false;
    }
  }
  return true;
};

export const areDateRangesOverlapping = (a: DateRange, b: DateRange) => {
  const aStart = dayjs(a.start);
  const aEnd = dayjs(a.end);
  const bStart = dayjs(b.start);
  const bEnd = dayjs(b.end);

  const isOverlapping =
    (aStart.isBefore(bEnd) || aStart.isSame(bEnd)) && (aEnd.isAfter(bStart) || aEnd.isSame(bStart));

  const aContainsB =
    (aStart.isBefore(bStart) || aStart.isSame(bStart)) && (aEnd.isAfter(bEnd) || aEnd.isSame(bEnd));

  const bContainsA =
    (bStart.isBefore(aStart) || bStart.isSame(aStart)) && (bEnd.isAfter(aEnd) || bEnd.isSame(aEnd));

  return isOverlapping || aContainsB || bContainsA;
};

export const areFloatsEqual = (a: number, b: number) => {
  return Math.abs(a - b) < 0.000001;
};

export const compareAnalysisNumber = (a: string, b: string) => {
  const parsedA = parseFloat(a.replace("-", ".").replace("b", "-"));
  const parsedB = parseFloat(b.replace("-", ".").replace("b", "-"));

  if (isNaN(parsedA) || isNaN(parsedB)) {
    return a.localeCompare(b);
  }

  return parsedA - parsedB;
};

// kenny: 배열에 포함되지 않은 key들은 직접 변경되거나 변경되지 않음
const COMPARE_KEYS = [
  "analysisNumber",
  "floorPlanId",
  "position",
  "adjustmentConfig",
  "additionConfig",
  "countingLines",
  "intervalZones",
  "memos",
] as const;
export const getCameraAndAnalysisConfigDiff = (
  origin: CameraWithAnalysisConfig,
  local: CameraWithAnalysisConfig
) => {
  const diff: CameraAndAnalysisConfigDiff = {
    cameraId: local.id,
    cameraDiff: {},
    countingLinesDiff: {
      created: [],
      updated: [],
      deleted: [],
    },
    intervalZonesDiff: {
      created: [],
      updated: [],
      deleted: [],
    },
    memosDiff: {
      created: [],
      updated: [],
      deleted: [],
    },
  };

  COMPARE_KEYS.forEach((key) => {
    switch (key) {
      case "analysisNumber": {
        if (origin[key] !== local[key]) {
          diff.cameraDiff[key] = local[key];
        }
        break;
      }
      case "floorPlanId": {
        if (origin[key] !== local[key]) {
          diff.cameraDiff[key] = local[key];
        }
        break;
      }
      case "position": {
        const originPosition = origin[key];
        const localPosition = local[key];
        if (
          (isNullish(localPosition) && !isNullish(originPosition)) ||
          (!isNullish(localPosition) && isNullish(originPosition))
        ) {
          diff.cameraDiff[key] = localPosition;
        } else if (!isNullish(localPosition) && !isNullish(originPosition)) {
          if (!arePositionsEqual(originPosition, localPosition)) {
            diff.cameraDiff[key] = localPosition;
          }
        }
        break;
      }
      case "adjustmentConfig": {
        const originAdjustmentConfig = origin[key];
        const localAdjustmentConfig = local[key];
        if (
          (isNullish(localAdjustmentConfig) && !isNullish(originAdjustmentConfig)) ||
          (!isNullish(localAdjustmentConfig) && isNullish(originAdjustmentConfig))
        ) {
          diff.cameraDiff[key] = localAdjustmentConfig;
        } else if (!isNullish(localAdjustmentConfig) && !isNullish(originAdjustmentConfig)) {
          if (
            originAdjustmentConfig.adjustmentId !== localAdjustmentConfig.adjustmentId ||
            !arePositionsEqual(originAdjustmentConfig.position, localAdjustmentConfig.position) ||
            !areFloatsEqual(originAdjustmentConfig.frame.dx, localAdjustmentConfig.frame.dx) ||
            !areFloatsEqual(originAdjustmentConfig.frame.dy, localAdjustmentConfig.frame.dy)
          ) {
            diff.cameraDiff[key] = localAdjustmentConfig;
          }
        }
        break;
      }
      case "additionConfig": {
        const originAdditionConfig = origin[key];
        const localAdditionConfig = local[key];
        if (
          (isNullish(localAdditionConfig) && !isNullish(originAdditionConfig)) ||
          (!isNullish(localAdditionConfig) && isNullish(originAdditionConfig))
        ) {
          diff.cameraDiff[key] = localAdditionConfig;
        } else if (!isNullish(localAdditionConfig) && !isNullish(originAdditionConfig)) {
          if (originAdditionConfig.additionId !== localAdditionConfig.additionId) {
            diff.cameraDiff[key] = localAdditionConfig;
          }
        }
        break;
      }
      case "countingLines": {
        const originCountingLines = origin[key];
        const localCountingLines = local[key];

        localCountingLines.forEach((localCountingLine) => {
          const originCountingLine = originCountingLines.find(
            (originCountingLine) => originCountingLine.id === localCountingLine.id
          );
          if (!originCountingLine) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...rest } = localCountingLine;
            diff.countingLinesDiff.created.push(rest);
          } else if (!areCountingLinesEqual(originCountingLine, localCountingLine)) {
            diff.countingLinesDiff.updated.push(localCountingLine);
          }
        });
        originCountingLines.forEach((originCountingLine) => {
          const localCountingLine = localCountingLines.find(
            (localCountingLine) => localCountingLine.id === originCountingLine.id
          );
          if (!localCountingLine) {
            diff.countingLinesDiff.deleted.push(originCountingLine);
          }
        });

        break;
      }
      case "intervalZones": {
        const originIntervalZones = origin[key];
        const localIntervalZones = local[key];

        localIntervalZones.forEach((localIntervalZone) => {
          const originIntervalZone = originIntervalZones.find(
            (originIntervalZone) => originIntervalZone.id === localIntervalZone.id
          );
          if (!originIntervalZone) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...rest } = localIntervalZone;
            diff.intervalZonesDiff.created.push(rest);
          } else if (!areIntervalZonesEqual(originIntervalZone, localIntervalZone)) {
            diff.intervalZonesDiff.updated.push(localIntervalZone);
          }
        });
        originIntervalZones.forEach((originIntervalZone) => {
          const localIntervalZone = localIntervalZones.find(
            (localIntervalZone) => localIntervalZone.id === originIntervalZone.id
          );
          if (!localIntervalZone) {
            diff.intervalZonesDiff.deleted.push(originIntervalZone);
          }
        });
        break;
      }
      case "memos": {
        const originMemos = origin[key];
        const localMemos = local[key];

        localMemos.forEach((localMemo) => {
          const originMemo = originMemos.find((originMemo) => originMemo.id === localMemo.id);
          if (!originMemo) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { id, ...rest } = localMemo;
            diff.memosDiff.created.push(rest);
          } else if (!areCameraMemosEqual(originMemo, localMemo)) {
            diff.memosDiff.updated.push(localMemo);
          }
        });
        originMemos.forEach((originMemo) => {
          const localMemo = localMemos.find((localMemo) => localMemo.id === originMemo.id);
          if (!localMemo) {
            diff.memosDiff.deleted.push(originMemo);
          }
        });
        break;
      }
    }
  });

  return diff;
};

export const getFloorPlanMemoDiagramsDiff = (
  origin: FloorPlanMemoDiagram[],
  local: FloorPlanMemoDiagram[]
) => {
  const diff: FloorPlanDiff["memoDiagramsDiff"] = {
    created: [],
    updated: [],
    deleted: [],
  };

  local.forEach((localDiagram) => {
    const originDiagram = origin.find((originDiagram) => originDiagram.id === localDiagram.id);
    if (!originDiagram) {
      diff.created.push(localDiagram);
    } else if (!areFloorPlanMemoDiagramsEqual(originDiagram, localDiagram)) {
      diff.updated.push(localDiagram);
    }
  });
  origin.forEach((originDiagram) => {
    const localDiagram = local.find((localDiagram) => localDiagram.id === originDiagram.id);
    if (!localDiagram) {
      diff.deleted.push(originDiagram.id);
    }
  });

  return diff;
};

export const getFloorPlanMemoTextsDiff = (
  origin: FloorPlanMemoText[],
  local: FloorPlanMemoText[]
) => {
  const diff: FloorPlanDiff["memoTextsDiff"] = {
    created: [],
    updated: [],
    deleted: [],
  };

  local.forEach((localText) => {
    const originText = origin.find((originText) => originText.id === localText.id);
    if (!originText) {
      diff.created.push(localText);
    } else if (!areFloorPlanMemoTextsEqual(originText, localText)) {
      diff.updated.push(localText);
    }
  });
  origin.forEach((originText) => {
    const localText = local.find((localText) => localText.id === originText.id);
    if (!localText) {
      diff.deleted.push(originText.id);
    }
  });
  return diff;
};

export const isNullish = (value: unknown): value is null | undefined => {
  return value === undefined || value === null;
};

// kenny: 레거시 백엔드에서 position 기본 값으로 빈 객체를 사용하던 것을 호환하기 위해 추가
export const isNullishOrEmptyObject = (
  value: unknown
): value is null | undefined | Record<string, never> => {
  return isNullish(value) || Object.keys(value as object).length === 0;
};
